/**
 * Widget "Mes taches" pour le tableau de bord.
 * Affiche les taches non terminees + checkbox done + suppression.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTeamHandles } from '../hooks/useTeamHandles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isOverdue = (due) => {
  if (!due) return false;
  return due < new Date().toISOString().slice(0, 10);
};

export const MyTasksWidget = () => {
  const navigate = useNavigate();
  const teamHandles = useTeamHandles();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCount, setOpenCount] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newAssignee, setNewAssignee] = useState('julie');
  const [newDue, setNewDue] = useState('');
  const [newProject, setNewProject] = useState('');
  const [projects, setProjects] = useState([]);
  // Mini-modale Repondre : { task_id, task_title, text, mark_done, sending }
  const [replyState, setReplyState] = useState(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tasks/me`);
      setTasks(r.data.tasks || []);
      setOpenCount(r.data.open_count || 0);
      setOverdueCount(r.data.overdue_count || 0);
    } catch {} finally { setLoading(false); }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects`);
      setProjects(r.data || []);
    } catch {}
  }, []);

  useEffect(() => { fetchTasks(); fetchProjects(); }, [fetchTasks, fetchProjects]);

  const toggle = async (t) => {
    try {
      await axios.patch(`${API}/tasks/${t.task_id}`, { done: !t.done });
      fetchTasks();
    } catch {}
  };

  const remove = async (t) => {
    if (!window.confirm('Supprimer cette tache ?')) return;
    try {
      await axios.delete(`${API}/tasks/${t.task_id}`);
      fetchTasks();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    }
  };

  const openReply = async (t) => {
    setReplyState({ task_id: t.task_id, task_title: t.title, text: '', mark_done: false, sending: false, replies: [], loadingReplies: true });
    try {
      const r = await axios.get(`${API}/tasks/${t.task_id}/replies`);
      setReplyState(prev => prev ? { ...prev, replies: r.data.replies || [], loadingReplies: false } : prev);
    } catch {
      setReplyState(prev => prev ? { ...prev, loadingReplies: false } : prev);
    }
  };

  const submitReply = async () => {
    if (!replyState || !replyState.text.trim()) return;
    setReplyState({ ...replyState, sending: true });
    try {
      const r = await axios.post(`${API}/tasks/${replyState.task_id}/reply`, {
        message: replyState.text.trim(),
        mark_done: !!replyState.mark_done,
      });
      if (replyState.mark_done) {
        setReplyState(null);
        fetchTasks();
      } else {
        // Ajoute la reponse au fil sans fermer la modale
        const newReply = r.data?.reply;
        setReplyState(prev => prev ? {
          ...prev,
          text: '',
          sending: false,
          replies: newReply ? [...(prev.replies || []), newReply] : prev.replies,
        } : prev);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur envoi reponse');
      setReplyState({ ...replyState, sending: false });
    }
  };

  const create = async () => {
    if (!newTitle.trim()) return;
    try {
      await axios.post(`${API}/tasks`, {
        title: newTitle.trim(),
        assigned_to: newAssignee,
        due_date: newDue || null,
        project_id: newProject || null,
      });
      setNewTitle(''); setNewDue(''); setNewProject(''); setShowQuickAdd(false);
      fetchTasks();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="realease-card p-6" data-testid="my-tasks-widget">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <svg className="w-5 h-5 text-[#008A8D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          Mes taches
          <span className="text-xs px-2 py-0.5 rounded-full bg-[#008A8D]/10 text-[#006B6E]">{openCount}</span>
          {overdueCount > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700" data-testid="overdue-badge">{overdueCount} en retard</span>
          )}
        </h3>
        <button onClick={() => setShowQuickAdd(!showQuickAdd)}
                className="px-3 py-1.5 bg-[#008A8D] text-white text-sm font-semibold rounded-lg hover:bg-[#006B6D] flex items-center gap-1"
                data-testid="my-tasks-add-toggle">
          {showQuickAdd ? (
            <>Annuler</>
          ) : (
            <><span className="text-base leading-none">+</span> Nouvelle tache</>
          )}
        </button>
      </div>

      {showQuickAdd && (
        <div className="mb-4 p-3 bg-[#FDF8F0] rounded-lg border border-[#E8DCC4] space-y-2" data-testid="task-quick-add">
          <p className="text-xs font-semibold text-[#A57F4F]">Creer une tache - assignez-la a un coequipier et/ou a un projet.</p>
          <input value={newTitle} onChange={e => setNewTitle(e.target.value)}
                 placeholder="Que faut-il faire ? (ex: Relancer la banque)"
                 className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                 data-testid="task-quick-title" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-semibold text-gray-500 uppercase">Assigner a</label>
              <select value={newAssignee} onChange={e => setNewAssignee(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                      data-testid="task-quick-assignee">
                {teamHandles.map(h => (
                  <option key={h.handle} value={h.handle}>@{h.handle} - {h.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-500 uppercase">Projet (optionnel)</label>
              <select value={newProject} onChange={e => setNewProject(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                      data-testid="task-quick-project">
                <option value="">Tache globale</option>
                {projects.filter(p => p.status !== 'termine').map(p => (
                  <option key={p.project_id} value={p.project_id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-end gap-2 pt-1">
            <div className="flex-1">
              <label className="text-[11px] font-semibold text-gray-500 uppercase">Echeance</label>
              <input type="date" value={newDue} onChange={e => setNewDue(e.target.value)}
                     className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
                     data-testid="task-quick-due" />
            </div>
            <button onClick={create} disabled={!newTitle.trim()}
                    className="px-4 py-2 bg-[#008A8D] text-white text-sm font-semibold rounded-lg hover:bg-[#006B6D] disabled:opacity-50"
                    data-testid="task-quick-submit">
              Creer la tache
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2 max-h-80 overflow-y-auto">
        {loading && <p className="text-xs text-gray-400 italic">Chargement...</p>}
        {!loading && tasks.length === 0 && (
          <p className="text-xs text-gray-400 italic text-center py-3">Aucune tache pour vous. Bravo !</p>
        )}
        {tasks.map(t => {
          const overdue = isOverdue(t.due_date) && !t.done;
          return (
            <div key={t.task_id}
                 className={`p-3 border rounded-lg flex items-start gap-3 transition-all ${t.done ? 'bg-gray-50 border-gray-100 opacity-60' : overdue ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}
                 data-testid={`task-${t.task_id}`}>
              <input type="checkbox" checked={!!t.done} onChange={() => toggle(t)}
                     className="mt-1 w-4 h-4 accent-[#008A8D] cursor-pointer"
                     data-testid={`task-${t.task_id}-toggle`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`text-sm ${t.done ? 'line-through text-gray-400' : 'text-gray-900'}`}>{t.title}</p>
                  {t.unread_replies_count > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#13736D] text-white font-bold animate-pulse"
                          title={`${t.unread_replies_count} reponse(s) non lue(s)`}
                          data-testid={`task-${t.task_id}-unread`}>
                      +{t.unread_replies_count}
                    </span>
                  )}
                  {t.unread_replies_count === 0 && t.replies_count > 0 && (
                    <span className="text-[10px] text-gray-400 flex items-center gap-0.5"
                          title={`${t.replies_count} reponse(s) au total`}>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                      {t.replies_count}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-500 flex-wrap">
                  {t.project_name && (
                    <button onClick={() => navigate(`/projects/${t.project_id}`)}
                            className="hover:underline text-[#008A8D]">
                      {t.project_name}
                    </button>
                  )}
                  {t.due_date && (
                    <span className={overdue ? 'text-red-600 font-semibold' : ''}>
                      {overdue ? '⚠ ' : ''}Echeance : {t.due_date}
                    </span>
                  )}
                  <span className="text-gray-400">par {t.created_by_name}</span>
                </div>
              </div>
              <button onClick={() => openReply(t)}
                      className="text-[10px] px-2 py-1 rounded bg-[#13736D] text-white font-semibold hover:bg-[#0F5C57]"
                      title="Repondre au demandeur"
                      data-testid={`task-${t.task_id}-reply`}>
                Repondre
              </button>
              <button onClick={() => remove(t)}
                      className="text-gray-300 hover:text-red-500 text-xs"
                      data-testid={`task-${t.task_id}-delete`}>
                ✕
              </button>
            </div>
          );
        })}
      </div>

      {/* Mini-modale Repondre */}
      {replyState && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => !replyState.sending && setReplyState(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="my-tasks-reply-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Repondre au demandeur</h3>
            <p className="text-xs text-gray-500 mb-2">Sur la tache : <b>{replyState.task_title}</b></p>
            <p className="text-xs text-gray-500 mb-3">Votre message partira en notification + email.</p>

            {/* Fil de discussion */}
            {replyState.loadingReplies ? (
              <p className="text-xs text-gray-400 italic mb-3">Chargement de l'historique...</p>
            ) : replyState.replies && replyState.replies.length > 0 ? (
              <div className="mb-4 border-l-2 border-[#13736D]/30 pl-3 space-y-2 max-h-48 overflow-y-auto" data-testid="task-reply-history">
                <p className="text-[11px] uppercase font-semibold text-gray-500">Fil de discussion ({replyState.replies.length})</p>
                {replyState.replies.map(r => (
                  <div key={r.reply_id} className="bg-gray-50 rounded-lg p-2.5">
                    <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mb-1">
                      <span className="font-semibold text-gray-700">{r.from_user_name}</span>
                      {r.mark_done && <span className="text-emerald-600 font-semibold">a marque la tache terminee</span>}
                      <span className="ml-auto">{new Date(r.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{r.message}</p>
                  </div>
                ))}
              </div>
            ) : null}

            <textarea
              value={replyState.text}
              onChange={e => setReplyState({ ...replyState, text: e.target.value })}
              placeholder="C'est fait, tu peux continuer..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm h-28 focus:outline-none focus:ring-2 focus:ring-[#13736D]"
              autoFocus
            />
            <label className="flex items-center gap-2 text-sm text-gray-700 mt-3 cursor-pointer">
              <input type="checkbox"
                     checked={!!replyState.mark_done}
                     onChange={e => setReplyState({ ...replyState, mark_done: e.target.checked })}
                     className="w-4 h-4 accent-emerald-500" />
              Marquer la tache comme terminee
            </label>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setReplyState(null)} disabled={replyState.sending}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                Fermer
              </button>
              <button onClick={submitReply} disabled={replyState.sending || !replyState.text.trim()}
                      className="px-4 py-2 text-sm bg-[#13736D] text-white rounded-lg hover:bg-[#0F5C57] disabled:opacity-50 font-semibold">
                {replyState.sending ? 'Envoi...' : 'Envoyer la reponse'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTasksWidget;
